import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { TeamAvatar, ThemeToggle, LiveTag, fmtDateTime } from "@/components/common";
import { Card } from "@/components/ui/card";
import { TournamentResults, TournamentStandings } from "@/components/TournamentSummary";
import { Users, Megaphone, Lock } from "lucide-react";

export default function TeamView() {
  const { code } = useParams();
  const [d, setD] = useState(null);
  const [nf, setNf] = useState(false);

  const load = useCallback(() => {
    api.get(`/access/team/${code}`).then((r) => setD(r.data)).catch(() => setNf(true));
  }, [code]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const iv = setInterval(load, 10000); return () => clearInterval(iv); }, [load]);

  if (nf) return <Center>Invalid team link.</Center>;
  if (!d) return <Center>Loading…</Center>;

  const team = d.team;
  const ctx = d.context;
  const t = ctx.tournament;
  const primary = "hsl(var(--primary))";
  const isDraft = t.status === "draft";
  const myMatches = ctx.matches.filter((m) => (m.team_a && m.team_a.id === team.id) || (m.team_b && m.team_b.id === team.id));

  return (
    <div className="min-h-screen bg-background pb-10">
      <header className="flex items-center justify-between border-b p-4">
        <div className="flex items-center gap-3"><TeamAvatar team={team} size={40} /><div><div className="font-heading font-bold leading-tight">{team.name}</div><div className="text-xs text-muted-foreground">Team · {t.name}</div></div></div>
        <ThemeToggle />
      </header>

      <div className="mx-auto max-w-md space-y-6 p-4">
        <Card className="p-4">
          <h2 className="mb-3 flex items-center gap-2 font-heading font-bold"><Users className="h-4 w-4" /> Members ({team.members.length})</h2>
          {team.members.length === 0 ? <p className="text-sm text-muted-foreground">No members added yet.</p> : (
            <div className="flex flex-wrap gap-1.5">{team.members.map((m, i) => <span key={i} className="rounded-full bg-muted px-3 py-1 text-sm">{m.name}</span>)}</div>
          )}
          {team.captain_name && <p className="mt-3 text-xs text-muted-foreground">Captain: {team.captain_name}</p>}
        </Card>

        {isDraft ? <DraftNotice /> : (
          <div>
            <h2 className="text-label mb-2 text-muted-foreground">Our matches</h2>
            {myMatches.length === 0 ? <p className="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground">No matches scheduled yet.</p> : (
              <div className="space-y-2">{myMatches.map((m) => {
                const isFinal = m.status === "final";
                return (
                  <Card key={m.id} className="p-3" data-testid={`team-match-${m.id}`}>
                    <div className="mb-1 flex justify-between text-xs text-muted-foreground"><span>{m.round_name} · {m.field || "—"}</span>{isFinal ? <span className="flex items-center gap-1 text-emerald-600"><Lock className="h-3 w-3" /> FINAL</span> : m.status === "live" ? <LiveTag /> : <span>{fmtDateTime(m.scheduled_time, m.timezone)}</span>}</div>
                    <div className="flex justify-between"><span>{m.team_a?.name || m.team_a_placeholder}</span><span className="font-bold">{m.score_a ?? ""}</span></div>
                    <div className="flex justify-between"><span>{m.team_b?.name || m.team_b_placeholder}</span><span className="font-bold">{m.score_b ?? ""}</span></div>
                  </Card>
                );
              })}</div>
            )}
          </div>
        )}

        {!isDraft && <TournamentStandings context={ctx} teamId={team.id} />}

        {ctx.announcements?.length > 0 && (
          <div><h2 className="text-label mb-2 text-muted-foreground">Announcements</h2>
            {ctx.announcements.map((a) => <Card key={a.id} className="mb-2 flex gap-2 p-3"><Megaphone className="mt-0.5 h-4 w-4" style={{ color: primary }} /><div><div className="font-semibold">{a.title}</div><div className="text-sm text-muted-foreground">{a.body}</div></div></Card>)}
          </div>
        )}


        {!isDraft && <TournamentResults context={ctx} />}
      </div>
    </div>
  );
}

const Center = ({ children }) => <div className="flex h-screen items-center justify-center text-muted-foreground">{children}</div>;
const DraftNotice = () => <p className="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground">This tournament is still in draft.</p>;
